/**
 * Scroll parallax, ticked from the Lenis rAF loop in app.js so the offset stays
 * in step with the smooth-scroll position instead of trailing it.
 *
 * Cover mode — the image is sized taller than the element that clips it (see
 * --parallax-size in app.css) and slides through exactly that measured overflow
 * while the clip crosses the viewport, so an edge can never be exposed:
 *
 *     <div class="parallax-clip h-96">
 *         <img data-parallax class="w-full object-cover" src="...">
 *     </div>
 *
 * Shift mode — a symmetric drift for decor that is not cropped. Stack a few
 * with different amounts to fake depth:
 *
 *     <img data-parallax data-parallax-shift="8vmin" src="...">
 *
 * Optional attributes: data-parallax-measure="<selector>" tracks a different
 * element's position (nearest match going up the tree), data-parallax-amount
 * uses only part of the available overflow in cover mode.
 */

const items = [];
const byElement = new Map();

let inViewObserver = null;
let sizeObserver = null;
let needsMeasure = false;

const clamp01 = (value) => (value < 0 ? 0 : value > 1 ? 1 : value);

// Out-of-view items are skipped, but only once they have been placed at their
// clamped start offset — otherwise they pop the first time they scroll in.
const isActive = (item) =>
    item.inView || !item.measured || item.applied === null;

const prefersReducedMotion = () =>
    matchMedia("(prefers-reduced-motion: reduce)").matches;

function measureElement(el) {
    const selector = el.dataset.parallaxMeasure;

    if (selector) {
        return (
            el.closest(selector) ||
            document.querySelector(selector) ||
            el.parentElement
        );
    }

    return el.closest(".parallax-clip") || el.parentElement;
}

/**
 * Registers every [data-parallax] inside root. Safe to call again after
 * injecting markup — elements already registered are skipped.
 */
export function initParallax(root = document) {
    if (prefersReducedMotion()) return;

    if (!inViewObserver) {
        inViewObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const item = byElement.get(entry.target);
                    if (item) item.inView = entry.isIntersecting;
                });
            },
            { rootMargin: "15% 0px" },
        );

        sizeObserver = new ResizeObserver(() => {
            needsMeasure = true;
        });

        window.addEventListener("resize", () => {
            needsMeasure = true;
        });
    }

    root.querySelectorAll("[data-parallax]").forEach((el) => {
        if (byElement.has(el)) return;

        const item = {
            el,
            clip: el.parentElement,
            measure: measureElement(el),
            shift: el.dataset.parallaxShift || null,
            amount: parseFloat(el.dataset.parallaxAmount) || 1,
            overflow: 0,
            progress: 0,
            measured: false,
            inView: false,
            applied: null,
        };

        if (item.shift) el.style.setProperty("--parallax-shift", item.shift);

        items.push(item);
        byElement.set(el, item);
        inViewObserver.observe(el);
        sizeObserver.observe(item.measure);

        // A lazily loaded image has no height until it arrives.
        if (el.tagName === "IMG" && !el.complete) {
            el.addEventListener(
                "load",
                () => {
                    needsMeasure = true;
                },
                { once: true },
            );
        }
    });

    needsMeasure = true;
}

/**
 * How far the element can travel inside its clip. Measured from fractional
 * box heights, not offsetHeight/clientHeight — those round to whole pixels,
 * which over-reports the overflow and opens a sub-pixel gap at the far edge.
 */
function measureOverflow(item) {
    const style = getComputedStyle(item.clip);

    const clipHeight =
        item.clip.getBoundingClientRect().height -
        parseFloat(style.paddingTop) -
        parseFloat(style.paddingBottom) -
        parseFloat(style.borderTopWidth) -
        parseFloat(style.borderBottomWidth);

    return Math.max(0, item.el.getBoundingClientRect().height - clipHeight);
}

export function updateParallax() {
    if (!items.length) return;

    if (needsMeasure) {
        items.forEach((item) => (item.measured = false));
        needsMeasure = false;
    }

    const viewport = window.innerHeight;

    // Read pass. Kept separate from the writes below so the browser is never
    // asked to re-layout mid-loop.
    for (const item of items) {
        if (!isActive(item)) continue;

        if (!item.measured) {
            item.overflow = item.shift ? 0 : measureOverflow(item);
            item.measured = true;
        }

        const rect = item.measure.getBoundingClientRect();

        // 0 as the element's top edge enters at the bottom of the viewport,
        // 1 as its bottom edge leaves at the top.
        item.progress = clamp01(
            1 - (rect.top + rect.height) / (viewport + rect.height),
        );
    }

    // Write pass.
    for (const item of items) {
        if (!isActive(item)) continue;

        if (item.shift) {
            const t = Math.round((1 - item.progress * 2) * 1000) / 1000;
            if (t === item.applied) continue;
            item.applied = t;
            item.el.style.setProperty("--parallax-t", t);
            continue;
        }

        const y =
            Math.round(
                -item.overflow * item.amount * (1 - item.progress) * 100,
            ) / 100;

        if (y === item.applied) continue;
        item.applied = y;
        item.el.style.setProperty("--parallax-y", `${y}px`);
    }
}
